import { type ReadableAtom } from 'nanostores';
import { clientFetch } from '~/lib/.client/fetch';
import type { JSONPatch, PatchOperation } from '~/lib/.server/utils/json-patch';
import { settingsGlobalStore } from '~/lib/stores/settings';
import { DEFAULT_SETTINGS, type APISettings, type Settings } from '~/types/settings';
import { createScopedLogger } from '~/utils/logger';
import { NOOP } from '~/utils/noops';

const logger = createScopedLogger('SettingsService');

const LOCAL_STORAGE_KEY = 'bolt_settings:';

/**
 * Service for managing application settings. Handles synchronization between client-side state
 * and server persistence.
 *
 * Features:
 *  - Maintains settings in a reactive store using nanostores
 *  - Syncs settings with backend API via PATCH requests
 *  - Handles optimistic updates with rollback on failure
 *  - Migrates legacy settings from localStorage to the API
 *  - Provides type-safe access to settings values
 *
 * @todo Would be nice to create an abstraction on top of nanostore as suggested by Joan.
 */
class SettingsService {
  #initialized = false;
  #store = settingsGlobalStore;

  get settingsStore() {
    return this.#store as ReadableAtom<Settings>;
  }

  /**
   * Must be called first! The settings come from the backend and are provided via the loader data
   * higher up in the component tree.
   */
  async init(apiSettings: APISettings = {}) {
    if (this.#initialized) {
      return;
    }

    this.#initialized = true;

    if (import.meta.env.DEV) {
      logger.debug('API settings', apiSettings);
    }

    for (const category in apiSettings) {
      const castedCategory = category as keyof Settings;

      this.#store.setKey(castedCategory, {
        ...DEFAULT_SETTINGS[castedCategory],
        ...apiSettings[castedCategory],
      });
    }

    /**
     * Persisting settings to local storage is deprecated.
     */
    if (!import.meta.env.SSR) {
      this.#migrateLegacyStorageSettings(apiSettings);
    }
  }

  async loadSettings() {
    const response = await clientFetch('/api/settings');

    if (!response.ok) {
      throw new Error('Failed to load account settings');
    }

    const apiSettings = await response.json<APISettings>();

    this.#initialized = false;

    this.init(apiSettings);
  }

  async set<Category extends keyof Settings, Name extends Exclude<keyof Settings[Category], symbol>>(
    category: Category,
    name: Name,
    value: Settings[Category][Name],
  ) {
    const currentSettings = this.#store.get();
    const oldValue = currentSettings[category][name];

    this.#store.setKey(category, {
      ...currentSettings[category],
      [name]: value,
    });

    const response = await patchSettings([{ op: 'add', path: `/${category}/${name}`, value }] satisfies JSONPatch);

    if (!response.ok || response.status !== 204) {
      this.#store.setKey(category, {
        ...this.#store.get()[category],
        [name]: oldValue,
      });

      throw new Error('Failed to add or update settings');
    }
  }

  async setMultiple<Category extends keyof Settings>(category: Category, values: Partial<Settings[Category]>) {
    const currentSettings = this.#store.get();
    const oldValues = currentSettings[category];

    this.#store.setKey(category, {
      ...oldValues,
      ...values,
    });

    const response = await patchSettings(
      Object.entries(values).map(([key, value]) => {
        return { op: 'add', path: `/${category}/${key}`, value };
      }) satisfies JSONPatch,
    );

    if (!response.ok || response.status !== 204) {
      this.#store.setKey(category, {
        ...this.#store.get()[category],
        ...oldValues,
      });

      throw new Error('Failed to add or update settings');
    }
  }

  readValue<Category extends keyof Settings, Name extends Exclude<keyof Settings[Category], symbol>>(
    category: Category,
    name: Name,
  ) {
    return this.#store.get()[category][name];
  }

  /**
   * Utility function to migrate all legacy local storage settings.
   *
   * Checks each category in localStorage and migrates any found settings
   * that aren't already set in the API settings.
   */
  #migrateLegacyStorageSettings(apiSettings: APISettings) {
    const legacyKeys = Object.keys(localStorage).filter((key) => key.startsWith(LOCAL_STORAGE_KEY));

    const patches: JSONPatch = [];

    const currentSettings = this.#store.get();

    for (const key of legacyKeys) {
      // extract category from the key (everything after the prefix)
      const category = key.slice(LOCAL_STORAGE_KEY.length) as keyof Settings;
      const legacyValue = localStorage.getItem(key);

      if (legacyValue == null) {
        localStorage.removeItem(key);
        continue;
      }

      try {
        const parsedValue = JSON.parse(legacyValue) as Settings[typeof category];
        const currentCategory = currentSettings[category];

        // only merge values that don't exist or are undefined in current settings
        const filteredLegacyValues = Object.entries(parsedValue).filter(([key]) => {
          const name = key as keyof typeof currentCategory;

          return apiSettings[category]?.[name] === undefined && parsedValue[name] !== undefined;
        });

        if (filteredLegacyValues.length) {
          patches.push(
            ...filteredLegacyValues.map(([key, value]) => {
              return {
                op: 'add',
                path: `/${category}/${key}`,
                value,
              } satisfies PatchOperation;
            }),
          );

          // merge the legacy values with current settings, only for properties that aren't already set in the store
          this.#store.setKey(category, {
            ...currentSettings[category],
            ...Object.fromEntries(filteredLegacyValues),
          });
        }
      } catch {
        // ignore parsing errors
      } finally {
        localStorage.removeItem(key);
      }
    }

    if (patches.length) {
      if (import.meta.env.DEV) {
        logger.debug('Migrating settings', patches);
      }

      patchSettings(patches).catch(NOOP);
    }
  }
}

function patchSettings(patches: JSONPatch) {
  return clientFetch('/api/settings', {
    method: 'PATCH',
    body: JSON.stringify(patches),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export const settingsService = new SettingsService();

if (import.meta.env.DEV && import.meta.env.SSR === false) {
  (window as any).__BOLT_SETTINGS_SERVICE__ = settingsService;
}
